import { LitElement, html, css } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';

import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';

import {
  converTemplateTypeToReadable,
  TemplateTypeEmail,
} from '../../util/util';
import { showNotification } from '../../LanghoutBetonPortal';
import { Template, TemplatePreview } from '../../types/types';

import '../../elements/langhout-button/langhout-button';
import { closeIcon } from '../../elements/icons/icons';
import { sharedStyles } from '../../elements/shared-styles';

@customElement('langhout-beton-update-template')
export class UpdateTemplate extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        font-size: 17px;
        color: var(--text-color-main);
        height: calc(100vh - 84px);
        max-height: calc(100vh - 84px);
        overflow-y: auto;
        background-color: white;
        z-index: 100;
        padding: 10px;
        padding-left: 30px;
        padding-right: 30px;
      }

      :host([disableButtons]) {
        pointer-events: none;
        opacity: 0.7;
      }

      .row {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-grow: 1;
      }

      .centered {
        justify-content: center;
      }

      .wrapper {
        display: flex;
        flex-direction: row;
        justify-content: center;
      }

      .properties-wrapper {
        margin-bottom: 40px;
      }

      .close-wrapper {
        padding: 5px;
      }

      .label {
        text-align: left;
        min-width: 180px;
        font-weight: bold;
        flex-grow: 1;
        max-width: 300px;
      }

      .value {
        text-align: right;
        min-width: 100px;
        flex-grow: 1;
        max-width: 300px;
      }

      lit-ace {
        height: 800px;
      }

      langhout-button {
        margin-top: 20px;
      }

      .save-button-wrapper {
        padding-bottom: 100px;
      }
    `,
  ];

  private previewTimer = 0;

  @property({ type: Object, reflect: true })
  public currentTemplate: Template = <Template>{};

  @property({ type: Object, reflect: true })
  public templatePreview: TemplatePreview = <TemplatePreview>{};

  @query('#editor')
  // eslint-disable-next-line
  public editor?: any;

  private currentContent = '';

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  constructor() {
    super();
  }

  attributeChangedCallback(
    name: string,
    oldVal: string | null,
    newVal: string | null,
  ) {
    super.attributeChangedCallback(name, oldVal, newVal);
    if (name === 'currenttemplate') {
      if (newVal) {
        const template = <Template>JSON.parse(newVal);
        if (template.content) {
          this.currentContent = this.currentTemplate.content;
          this.previewTemplate(this.currentTemplate.content);
        }
        if (template.id) {
          this.editor._setValue(this.currentTemplate.content);
        }
      }
    }
  }

  render() {
    return html`
      <div class="row">
        <div class="fill"></div>
        <div
          class="close-wrapper"
          @click="${() => {
            this.dispatchCloseEvent();
          }}"
        >
          ${closeIcon()}
        </div>
      </div>
      <div class="wrapper">
        <div class="properties-wrapper">
          <div class="row centered">
            <h2>Template aanpassen</h2>
          </div>
          <div class="row centered display-props">
            <div class="label">Naam:</div>
            <div class="value">${this.currentTemplate.name}</div>
          </div>
          <div class="row centered display-props">
            <div class="label">Type:</div>
            <div class="value">
              ${converTemplateTypeToReadable(this.currentTemplate.type)}
            </div>
          </div>
        </div>
      </div>
      <div class="wrapper">
        <lit-ace
          @editor-change="${(e: CustomEvent) => {
            if (e.detail.value === this.currentTemplate.content) {
              return;
            }
            this.currentContent = e.detail.value;
            this.previewWithBackoff(e.detail.value);
          }}"
          id="editor"
          baseUrl="node_modules/@f0rce/ace-builds/src-min-noconflict"
          theme="monokai"
          editor="vscode"
          mode="html"
        ></lit-ace>
        ${this.renderTemplate(this.templatePreview.content)}
      </div>
      <div class="wrapper save-button-wrapper">
        <langhout-button
          id="saveButton"
          text="Template opslaan"
          @click="${async () => {
            this.disableButtons = true;
            await this.updateTemplate();
            this.disableButtons = false;
          }}"
        >
        </langhout-button>
      </div>
    `;
  }

  renderTemplate(template: string) {
    if (template === '' || template === undefined || template === null) {
      return html`<div class="row centered">
        <h3>Voer een template in om een preview te zien</h3>
      </div>`;
    }
    if (this.currentTemplate.type === TemplateTypeEmail) {
      return html` <iframe
        style="width: 100%; height: 800px;"
        src="data:text/html;charset=utf-8,${template}#navpanes=0"
      ></iframe>`;
    }
    return html` <iframe
      style="width: 100%; height: 800px;"
      src="data:application/pdf;base64,${template}#navpanes=0"
    ></iframe>`;
  }

  updateTemplate = async () => {
    const path = `/templates/${this.currentTemplate.id}`;
    const payload = {
      id: this.currentTemplate.id,
      content: this.currentContent,
    };
    const result = await authenticatedAPICall(
      path,
      'PUT',
      new Headers(),
      JSON.stringify(payload),
    );
    if (isSuccessfulAPIResponse(result) && result.body.success) {
      showNotification('Template successvol opgeslagen!');
      this.dispatchReloadEvent();
      this.scrollTo(0, 0);
    }
  };

  previewWithBackoff = (content: string) => {
    clearInterval(this.previewTimer);
    this.previewTimer = window.setTimeout(() => {
      this.previewTemplate(content);
    }, 500);
  };

  previewTemplate = async (content: string) => {
    if (content === '') {
      return;
    }
    const path = `/templates/preview`;

    const result = await authenticatedAPICall(
      path,
      'POST',
      new Headers(),
      JSON.stringify({
        content: content,
        type: this.currentTemplate.type,
      }),
    );
    if (isSuccessfulAPIResponse(result)) {
      this.templatePreview = result.body;
    }
  };

  dispatchReloadEvent = () => {
    const event = new CustomEvent('reload', {
      detail: {},
    });
    this.dispatchEvent(event);
  };

  dispatchCloseEvent = () => {
    const event = new CustomEvent('close', {
      detail: {},
    });
    this.dispatchEvent(event);
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-update-template': UpdateTemplate;
  }
}
